import { computed, type Ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";
import type { PartnerIssue } from "@/types/project-types";

/**
 * Query hooks + presentation helpers for partner issues, shared across the dashboard widget
 * (`IssuesList.vue`), the full Issues page (`PartnerIssuesView.vue`), and the client detail page.
 * The pure helpers are safe to call from templates or DataTables render callbacks.
 */

/** Bootstrap icon class for an issue, picked by its type. */
export function issueIconClass(type: PartnerIssue["type"] | string): string {
  switch (type) {
    case "incomplete_config": return "bi-gear";
    case "failed_runs": return "bi-x-circle";
    case "authentication": return "bi-shield-exclamation";
    default: return "bi-exclamation-triangle";
  }
}

/** Bootstrap text-color class matching the issue's severity. */
export function issueSeverityColor(severity: PartnerIssue["severity"] | string): string {
  switch (severity) {
    case "critical":
    case "high": return "text-danger";
    case "medium": return "text-warning";
    case "low": return "text-info";
    default: return "text-secondary";
  }
}

/** Bootstrap `bg-*` badge class matching the issue's severity. */
export function issueSeverityBadgeClass(severity: PartnerIssue["severity"] | string): string {
  switch (severity) {
    case "critical":
    case "high": return "bg-danger";
    case "medium": return "bg-warning";
    case "low": return "bg-info";
    default: return "bg-secondary";
  }
}

/**
 * Where a partner should land to fix a given issue, after switching into the client's org. Deep-links
 * straight to the offending item when the issue carries the pointers (latest run / connection):
 *   - workflow-run failure → the exact failing run (history/:workflowid/:runid)
 *   - connection auth failure → the offending connection (connection-detail)
 * Falls back to the relevant tab (run history / connections / wizard) when those ids are absent,
 * then to the project overview, then home.
 */
export function issueFixRoute(
  issue: PartnerIssue,
): { name: string; params?: Record<string, string> } {
  // Connection deep-link doesn't need a project.
  if (issue.type === "authentication" && issue.connectionId) {
    return { name: "connection-detail", params: { connectionid: issue.connectionId } };
  }

  if (!issue.projectId) return { name: "home" };
  const params: Record<string, string> = { projectid: issue.projectId };

  switch (issue.type) {
    case "failed_runs":
      // Deep-link to the exact run when we have both the workflow and run ids.
      return issue.workflowId && issue.runId
        ? { name: "project-history-run", params: { ...params, workflowid: issue.workflowId, runid: issue.runId } }
        : { name: "project-history", params };
    case "authentication":
      return { name: "project-connections", params };
    case "incomplete_config":
      return { name: "project-wizard", params };
    default:
      return { name: "project-overview", params };
  }
}

/** "3h ago" / "2d ago" rendering for issue timestamps. */
export function formatIssueTimeAgo(timestamp: string): string {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

/**
 * Type options for issue filter dropdowns. Values are the lowercase `PartnerIssue["type"]` aliases
 * the hook produces (so client-side filtering on `i.type` matches), mapped from the backend enums:
 * failed_runs ← WorkflowRunFailure, authentication ← ConnectionAuthFailure, incomplete_config ← IncompleteConfig.
 */
export const ISSUE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "failed_runs", label: "Workflow Run Failure" },
  { value: "authentication", label: "Authentication" },
  { value: "incomplete_config", label: "Incomplete Configuration" },
];

/** Severity options for issue filter dropdowns. */
export const ISSUE_SEVERITY_OPTIONS: { value: string; label: string }[] = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

// =====================================================================
// Backend wiring — GET/POST against /api/partners/issues.
// =====================================================================

const BASE = `${import.meta.env.VITE_API_URL}/partners/issues`;

/** Shape returned by the API (PartnerIssueResponseDto). */
interface PartnerIssueRow {
  id: string;
  type: string; // PascalCase enum: WorkflowRunFailure | ConnectionAuthFailure | IncompleteConfig
  severity: string; // PascalCase enum: Low | Medium | High | Critical
  status: "Open" | "Resolved";
  title: string;
  description: string;
  projectId: string | null;
  projectName: string | null;
  clientOrgId: string;
  clientOrgName: string;
  dateOccurred: string;
  occurrenceCount: number;
  lastSeenAt: string;
  resolutionNote: string | null;
  resolvedDate: string | null;
  resolvedByUserId: string | null;
  workflowId: string | null;
  runId: string | null;
  connectionId: string | null;
}

// Map the backend enum names onto the lowercase `type` literals the components already render.
function mapType(type: string): PartnerIssue["type"] {
  switch (type) {
    case "WorkflowRunFailure": return "failed_runs";
    case "ConnectionAuthFailure": return "authentication";
    case "IncompleteConfig": return "incomplete_config";
    default: return "other";
  }
}

/** Map an API row to the component-facing `PartnerIssue`, filling the convenience aliases. */
function toPartnerIssue(row: PartnerIssueRow): PartnerIssue {
  return {
    id: row.id,
    type: mapType(row.type),
    severity: row.severity.toLowerCase() as PartnerIssue["severity"],
    title: row.title,
    description: row.description,
    projectId: row.projectId ?? undefined,
    projectName: row.projectName ?? undefined,
    clientId: row.clientOrgId,
    clientName: row.clientOrgName,
    dateOccurred: row.dateOccurred,
    resolved: row.status === "Resolved",
    resolvedDate: row.resolvedDate ?? undefined,
    status: row.status,
    clientOrgId: row.clientOrgId,
    clientOrgName: row.clientOrgName,
    occurrenceCount: row.occurrenceCount,
    lastSeenAt: row.lastSeenAt,
    resolutionNote: row.resolutionNote ?? undefined,
    resolvedByUserId: row.resolvedByUserId ?? undefined,
    workflowId: row.workflowId ?? undefined,
    runId: row.runId ?? undefined,
    connectionId: row.connectionId ?? undefined,
  };
}

function unwrap<T>(value: Ref<T> | T | undefined): T | undefined {
  if (value === undefined) return undefined;
  if (typeof value === "object" && value !== null && "value" in (value as object)) {
    return (value as Ref<T>).value;
  }
  return value as T;
}

export interface UsePartnerIssuesOptions {
  clientOrgId?: Ref<string | undefined> | string;
  projectId?: Ref<string | undefined> | string;
  /** Backend enum names, e.g. ["WorkflowRunFailure"]. */
  types?: Ref<string[] | undefined> | string[];
  /** Backend enum names, e.g. ["High","Critical"]. */
  severities?: Ref<string[] | undefined> | string[];
  /** "Open" | "Resolved". */
  status?: Ref<string | undefined> | string;
  take?: Ref<number | undefined> | number;
  enabled?: Ref<boolean> | boolean;
}

/**
 * Fetch partner issues — server scopes to the caller's active client orgs. Pass `clientOrgId` to
 * narrow to one client. Rows are mapped to the component-facing `PartnerIssue` shape.
 */
export function usePartnerIssues(options: UsePartnerIssuesOptions = {}) {
  const url = computed(() => {
    const params = new URLSearchParams();
    const clientOrgId = unwrap(options.clientOrgId);
    const projectId = unwrap(options.projectId);
    const types = unwrap(options.types);
    const severities = unwrap(options.severities);
    const status = unwrap(options.status);
    const take = unwrap(options.take);

    if (clientOrgId) params.set("clientOrgId", clientOrgId);
    if (projectId) params.set("projectId", projectId);
    for (const t of types ?? []) params.append("type", t);
    for (const s of severities ?? []) params.append("severity", s);
    if (status) params.set("status", status);
    if (take !== undefined) params.set("take", String(take));

    const qs = params.toString();
    return qs ? `${BASE}?${qs}` : BASE;
  });

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ["partner-issues", url],
    queryFn: async () => (await fetchWrapper.get<PartnerIssueRow[]>(url.value)).map(toPartnerIssue),
    enabled: typeof options.enabled === "boolean" ? options.enabled : (options.enabled ?? true),
  });

  const issues = computed(() => data.value ?? []);

  return {
    issues,
    issuesLoading: isFetching,
    issuesError: error as Ref<ApiError | null>,
    refetch,
  };
}

/** Count of OPEN partner issues across the caller's clients — dashboard badge. */
export function usePartnerIssueCount() {
  const { data, isFetching, error } = useQuery({
    queryKey: ["partner-issue-count"],
    queryFn: () => fetchWrapper.get<number>(`${BASE}/count`),
  });
  const count = computed(() => data.value ?? 0);
  return { count, countLoading: isFetching, countError: error as Ref<ApiError | null> };
}

function useInvalidateIssues() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["partner-issues"] });
    queryClient.invalidateQueries({ queryKey: ["partner-issue-count"] });
  };
}

/** Resolve an issue with a required note. */
export function useResolvePartnerIssue() {
  const invalidate = useInvalidateIssues();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<
    PartnerIssue,
    ApiError,
    { id: string; note: string }
  >({
    mutationFn: async ({ id, note }) =>
      toPartnerIssue(await fetchWrapper.post<PartnerIssueRow>(`${BASE}/${id}/resolve`, { note })),
    onSuccess: invalidate,
  });

  return {
    resolveIssue: mutate,
    resolveIssueAsync: mutateAsync,
    resolveIssuePending: isPending,
    resolveIssueError: error as Ref<ApiError | null>,
    resolveIssueReset: reset,
  };
}

/** Reopen a resolved issue. */
export function useReopenPartnerIssue() {
  const invalidate = useInvalidateIssues();
  const { mutate, mutateAsync, isPending, error, reset } = useMutation<PartnerIssue, ApiError, string>({
    mutationFn: async (id: string) =>
      toPartnerIssue(await fetchWrapper.post<PartnerIssueRow>(`${BASE}/${id}/reopen`, {})),
    onSuccess: invalidate,
  });

  return {
    reopenIssue: mutate,
    reopenIssueAsync: mutateAsync,
    reopenIssuePending: isPending,
    reopenIssueError: error as Ref<ApiError | null>,
    reopenIssueReset: reset,
  };
}
